import { useAuth0 } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useUserData } from "../contexts/user-data-context";
import { userQueryOptions } from "../lib/userQueries";
import * as RevenueCatService from "../services/revenueCatService";

/**
 * Component that fetches user profile data and populates the UserDataContext
 * This runs once when the user is authenticated
 */
export function UserDataLoader() {
	const { isAuthenticated, isLoading, user } = useAuth0();
	const { userData, updateUserData } = useUserData();
	const hasFetched = useRef(false);
	const queryClient = useQueryClient();

	useEffect(() => {
		// Only fetch if authenticated, not loading, and haven't fetched yet
		if (!isAuthenticated || isLoading || hasFetched.current || userData) {
			return;
		}

		// Mark as fetched immediately to prevent retries on error
		hasFetched.current = true;

		const fetchUserData = async () => {
			try {
				// Shared cache: dedupes with useUser / ably-provider so /api/user is
				// fetched once on load instead of once per consumer.
				const data = await queryClient.ensureQueryData(
					userQueryOptions(user?.sub),
				);

				// Update the context with the fetched data
				updateUserData({
					id: data.id,
					email: data.email,
					displayName: data.displayName || user?.name || "User",
					userImage: data.userImage || user?.picture || null,
					details: data.details || null,
				});

				// Configure/Link RevenueCat user with database user ID
				const platform = Capacitor.getPlatform();
				try {
					if (platform === "web") {
						await RevenueCatService.configure(data.id);
					} else {
						await RevenueCatService.setUserId(data.id);
					}
				} catch (_error) {
					console.warn(
						"Failed to configure RevenueCat - continuing without subscription features",
					);
				}
			} catch (error) {
				console.error("Error loading user data:", error);
				// Don't retry on error - hasFetched stays true to prevent loops
			}
		};

		fetchUserData();
	}, [isAuthenticated, isLoading, userData, updateUserData, user, queryClient]);

	return null; // This component doesn't render anything
}
